import {
  AppNavigationProvider,
  useAppNavigation,
} from '@euler/app/components/AppNavigationProvider';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { OrderSearchProvider } from '@euler/app/flows/search/context';
import { orderSearchScreens } from '@euler/app/flows/search/screens';
import { Aside, AsideRef } from '@euler/components/Aside';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { FontFamily } from '@euler/components/typography';
import { adaptiveWrapperOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ReceptionOrderStatus } from '@euler/model/enum';
import { SearchOrderResultItem } from '@euler/model/order/SearchOrderResultItem';
import { usePersistFn } from '@euler/utils/hooks';
import { getDefaultHeaderHeight } from '@react-navigation/elements';
import {
  CardStyleInterpolators,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import Color from 'color';
import Constants from 'expo-constants';
import { StatusBar } from 'expo-status-bar';
import { memo, useCallback, useRef } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { css, useTheme } from 'styled-components';

const Stack = createStackNavigator();

export const SearchOrdersFlow = wrapNavigatorScreen(
  ({
    defaultStatus,
    onPressResultItem,
  }: {
    defaultStatus?: ReceptionOrderStatus;
    onPressResultItem?: (item: SearchOrderResultItem) => void;
  }) => {
    const isMobile = useIsMobileLayout();
    const { width } = useWindowDimensions();
    const { rpx } = useResponsiveContext();
    const { bottom, top } = useSafeAreaInsets();

    const navigation = useAppNavigation();
    const asideRef = useRef<AsideRef>(null);
    const isAsideDismissedAsFinish = useRef(false);
    const finishCallback = useRef<() => void>();
    const resultItemRef = useRef<SearchOrderResultItem>();

    // this function is only expected to be called in nested stack screen
    // in the order flow navigation stack after everything is cleaned up.
    const finish = usePersistFn(
      (
        resultItem?: SearchOrderResultItem | undefined,
        callback?: () => void,
      ) => {
        if (asideRef.current) {
          // for aside, it's a two-step finish routine, first, we have to
          // dismiss the aside in an animated way, then when the animation
          // finishes, we remove the entire navigation stack and go back
          // to the main screen. So on finish is called, we first initiate
          // the animated dismiss of the aside.
          resultItemRef.current = resultItem;
          isAsideDismissedAsFinish.current = true;
          asideRef.current.dismiss();
          finishCallback.current = callback;
        } else {
          // for normal screen, we simply remove the entire navigation stack
          navigation.goBack();
          callback?.();
          if (resultItem != null) {
            onPressResultItem?.(resultItem);
          }
        }
      },
    );

    const onAsideDismissed = useCallback(() => {
      if (isAsideDismissedAsFinish.current) {
        navigation.goBack();
        finishCallback.current?.();
        finishCallback.current = undefined;
        isAsideDismissedAsFinish.current = false;
        if (resultItemRef.current) {
          onPressResultItem?.(resultItemRef.current);
          resultItemRef.current = undefined;
        }
        return true;
      }
      return false;
    }, [navigation, onPressResultItem]);

    const openOrderSearchResultItem = useCallback(
      (resultItem: SearchOrderResultItem) => {
        if (
          resultItem.status === ReceptionOrderStatus.Finished &&
          resultItem.serviceRecordId
        ) {
          navigation.push('ServiceRecord', {
            id: resultItem.serviceRecordId,
          });
        } else if (resultItem.taskNo) {
          navigation.push('Task', {
            taskNo: resultItem.taskNo,
          });
        }
      },
      [navigation],
    );

    if (isMobile) {
      return (
        <AppNavigationProvider>
          <OrderSearchProvider
            finish={finish}
            defaultStatus={defaultStatus}
            openOrderSearchResultItem={openOrderSearchResultItem}
          >
            <SafeAreaInsetsContext.Provider
              value={{
                left: 0,
                right: 0,
                top: Platform.select({ ios: top, default: top }),
                bottom,
              }}
            >
              <Content />
            </SafeAreaInsetsContext.Provider>
          </OrderSearchProvider>
        </AppNavigationProvider>
      );
    }
    return (
      <AppNavigationProvider>
        <OrderSearchProvider
          finish={finish}
          openOrderSearchResultItem={openOrderSearchResultItem}
        >
          <Aside
            ref={asideRef}
            dismissible={false}
            disableDefaultNavigationHandler={true}
            width={Math.min(750, Math.max(rpx(500), width * 0.45))}
            withoutKeyboardAvoidingView
            onDismissed={onAsideDismissed}
            css={`
              padding-top: ${0}px;
            `}
            backdropCss={css`
              background-color: rgba(0, 0, 0, 0.7);
            `}
          >
            <Content />
          </Aside>
        </OrderSearchProvider>
      </AppNavigationProvider>
    );
  },
  adaptiveWrapperOptions({
    cardStyleInterpolator: CardStyleInterpolators.forScaleFromCenterAndroid,
  }),
);

const Content = memo(() => {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const isMobile = useIsMobileLayout();
  const dimens = useWindowDimensions();
  const statusBarHeight = Constants.statusBarHeight;
  const headerHeight = getDefaultHeaderHeight(dimens, false, statusBarHeight);

  const tabletStyles = isMobile
    ? undefined
    : {
        headerStyle: {
          height: headerHeight,
        },
        headerLeftContainerStyle: {
          paddingTop: statusBarHeight,
        },
        headerRightContainerStyle: {
          paddingTop: statusBarHeight,
        },
        headerTitleContainerStyle: {
          paddingTop: statusBarHeight,
        },
      };
  return (
    <>
      <StatusBar style={'dark'} />
      <Stack.Navigator
        initialRouteName={'_index'}
        screenOptions={{
          ...TransitionPresets.SlideFromRightIOS,
          headerTitleAlign: 'center',
          headerBackTitle: ' ',
          headerTitleStyle: {
            fontSize: rpx(18),
            fontFamily: FontFamily.NotoSans.Bold,
          },
          cardStyle: {
            backgroundColor: Color(theme.page.card.backgroundColor)
              .lighten(0.04)
              .string(),
          },
          headerTitle: MultilineHeaderTitle,
          headerBackImage: () => <BackIcon />,
          ...tabletStyles,
        }}
      >
        {spreadScreens(Stack.Screen, orderSearchScreens, '_index', {})}
      </Stack.Navigator>
    </>
  );
});
